import EduButton from '@edulastic/common/src/components/Button'
import i18n from '@edulastic/localization'
import antNotification from 'antd/es/notification'
import React from 'react'
import styled from 'styled-components'
import { sanitizeUrl } from '../../utils/html'
import './notification.css'
import { calculateNotificationDuration } from './utils'

/**
 * Find the latest notification element
 * @returns {HTMLElement|null} The latest notification element or null
 */
const findLatestNotification = () => {
  const notificationElements = document.querySelectorAll(
    '.ant-notification-notice'
  )
  return notificationElements[notificationElements.length - 1] || null
}

/**
 * Set aria attributes on the notification element
 * @param {HTMLElement} element - The notification element
 * @param {string} ariaLabel - The aria-label attribute value
 * @param {string} ariaLive - The aria-live attribute value
 * @param {string} ariaRole - The role attribute value
 * @param {string} type - The notification type
 */
const setAriaAttributes = (element, ariaLabel, ariaLive, ariaRole, type) => {
  if (ariaLabel) {
    element.setAttribute('aria-label', ariaLabel)
  }
  if (ariaLive) {
    element.setAttribute('aria-live', ariaLive)
  }
  if (ariaRole) {
    element.setAttribute('role', ariaRole)
  }
  element.setAttribute('data-notification-type', type)
}

/**
 * Apply aria attributes to the notification element using MutationObserver
 * @param {string} ariaLabel - The aria-label attribute value
 * @param {string} ariaLive - The aria-live attribute value ('polite', 'assertive', or 'off')
 * @param {string} ariaRole - The role attribute value
 * @param {string} type - The notification type to help identify the element
 */
const applyAriaAttributes = (ariaLabel, ariaLive, ariaRole, type) => {
  // First, try to find existing notification immediately
  const existingNotification = findLatestNotification()
  if (existingNotification) {
    setAriaAttributes(existingNotification, ariaLabel, ariaLive, ariaRole, type)
    return
  }

  // If not found, use MutationObserver to watch for new notifications
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        const notification = findLatestNotification()
        if (notification) {
          setAriaAttributes(notification, ariaLabel, ariaLive, ariaRole, type)
          observer.disconnect() // Stop observing once we find the notification
        }
      }
    })
  })

  // Start observing the notification container
  const notificationContainer = document.querySelector('.ant-notification')
  if (notificationContainer) {
    observer.observe(notificationContainer, {
      childList: true,
      subtree: true,
    })
  }

  // Fallback: disconnect observer after 2 seconds to prevent memory leaks
  setTimeout(() => observer.disconnect(), 2000)
}

const defaultConf = {
  key: '',
  className: 'customized-notification',
  message: '',
  description: '',
  type: 'warn',
  buttonLink: '',
  buttonText: '',
  showButton: false,
  onClose: () => null,
  onClick: () => null,
  placement: 'bottomLeft',
  style: {
    width: 'fit-content',
    minWidth: 120,
  },
}

/**
 * @see https://ant.design/components/notification/
 * type is notification type.
 * messageKey is notification's message and description, we can get it from locales.
 * msg is custom message, if msg is passed, then will not use locales.
 * @param {{type?:'success' | 'error' | 'info' | 'warn' | 'warning', messageKey: String, showButton?:boolean, msg?:String, ariaLabel?:String, ariaLive?:'polite'|'assertive'|'off', ariaRole?:String }} options
 */
const notification = (options) => {
  const { messageKey, msg, exact, destroyAll, ...restOptions } = options
  // destroy existing notifications before showing new notification
  if (destroyAll) {
    antNotification.destroy()
  }
  // get messages from localization
  let translatedMessage =
    msg ||
    i18n.t(`notifications:${messageKey}.message`) ||
    'Something went wrong! '
  const translatedDescription = msg
    ? ''
    : i18n.t(`notifications:${messageKey}.description`)

  if (
    restOptions.type === 'error' &&
    translatedMessage !== 'Incorrect' &&
    !exact
  ) {
    translatedMessage = `${translatedMessage} Please try again later, or email support@assessment.peardeck.com.`
  }

  const config = {
    ...defaultConf,
    ...restOptions,
    message: translatedMessage,
    // ASM-749 - an explicit `description` wins. It used to be discarded whenever `msg` was set,
    // because this key lands after the `restOptions` spread.
    description: restOptions.description || translatedDescription || '',
  }

  const {
    type,
    description,
    showButton,
    buttonLink,
    buttonText,
    handlClickActionButton = () => {},
    ariaLabel,
    ariaLive,
    ariaRole,
    ...rest
  } = config

  const buttonProps = buttonLink
    ? {
        href: sanitizeUrl(buttonLink),
        target: '_blank',
      }
    : {
        onClick: handlClickActionButton,
      }
  if (antNotification[type] && config.message) {
    const messageTemplate = (
      <>
        {description ? <Description>{description}</Description> : <></>}
        {showButton && (
          <ActionButton {...buttonProps}>{buttonText}</ActionButton>
        )}
      </>
    )

    // Only calculate duration if not already provided in rest props
    const duration =
      rest.duration !== undefined
        ? rest.duration
        : calculateNotificationDuration(config.message, config.description)

    const notificationData = {
      description: messageTemplate,
      duration,
      ...rest,
    }

    // Show the notification
    antNotification[type](notificationData)

    // Apply aria attributes after notification is rendered
    if (ariaLabel || ariaLive || ariaRole) {
      applyAriaAttributes(ariaLabel, ariaLive, ariaRole, type)
    }
  }
}

// Memoized styled components to prevent recreation on every render
const Description = React.memo(styled.p`
  margin-top: 6px;
`)

const ActionButton = React.memo(styled(EduButton)`
  height: 30px;
  width: 150px;
  margin-top: 20px;
  margin-left: 0px;
  padding: 0px;
`)

export default notification
